import type { Extent } from "ol/extent";

import type { SearchEpicentersParams } from "../../../api/epicenter/search";
import { searchEpicenters } from "../../../api/epicenter/search";
import type { Epicenter } from "../../../api/epicenter/structs";
import { checkIn, checkOut } from "../../../utils/blocker";
import { Tabs } from "../../../utils/bulmautil/tabs";
import hx from "../../../utils/hydroxie";
import { InheritableEventEmitter } from "../../../utils/inheritableeventemitter";

import { FnetPanel } from "./panels/fnetpanel";
import { JmaPanel } from "./panels/jmapanel";

/**
 * 震源検索→検索条件エリア
 */
export class ConditionArea
    extends InheritableEventEmitter<{ update: [results: Epicenter[] | undefined] }> {

    /** 検索パラメーター */
    #searchParams?: Partial<SearchEpicentersParams>;
    /** タブコントロール */
    #tabs: Tabs;
    /** 気象庁震源パネル */
    #jmaPanel: JmaPanel;
    /** F-netパネル */
    #fnetPanel: FnetPanel;
    /** 検索ボタン要素 */
    #searchButton: HTMLElement;

    /**
     * コンストラクター。
     *
     * @param elem 検索条件エリアのルート要素
     */
    constructor(elem: HTMLElement) {
        super();

        const jmaPanelElem = hx("div", { id: "condition_jma", className: "condition-panel condition-jma" });
        this.#jmaPanel = new JmaPanel(jmaPanelElem);

        const fnetPanelElem = hx("div", { id: "condition_fnet", className: "condition-panel condition-fnet hidden" });
        this.#fnetPanel = new FnetPanel(fnetPanelElem);

        const tabElem = hx("div", { className: "tabs is-fullwidth" },
            hx("ul",
                hx("li", { className: "is-active" }, hx("a", "気象庁震源")),
                hx("li", hx("a", "F-netメカニズム解"))));
        this.#tabs = new Tabs({ rootElement: tabElem });

        const searchButton = hx("button", { id: "condition_search", className: "button" }, "検索");
        this.#searchButton = searchButton;

        elem.replaceChildren(tabElem, jmaPanelElem, fnetPanelElem, searchButton);
    }

    /**
     * 初期化処理を行う。
     * タブ切り替えや検索ボタンのイベントリスナーを設定する。
     */
    async init() {
        const panels = [
            this.#jmaPanel,
            this.#fnetPanel,
        ];

        this.#tabs.on("change", (_, index) => {
            for (const [i, panel] of panels.entries()) {
                if (i === index) {
                    panel.show();
                } else {
                    panel.hide();
                }
            }
        });

        this.#searchButton.addEventListener("click", async () => {
            try {
                checkIn("震源を検索中です。しばらくお待ち下さい。");
                const params = this.#searchParams;

                const results = await searchEpicenters(params);
                this.emit("update", results);
            } catch (e) {
                console.error(e);
                alert((Error.isError(e)) ? e.message : e);
            } finally {
                checkOut();
            }
        });

        for (const panel of panels) {
            panel.on("update", async (params) => {
                this.#searchParams = {
                    ...this.#searchParams,
                    ...params,
                };
            });
        }

        await Promise.all(panels.map(async (x) => x.init()));
    }

    /**
     * 地図上で選択された範囲を検索条件に設定する。
     *
     * @param extent 選択された範囲（Extent）
     */
    setExtent(extent: Extent) {
        this.#jmaPanel.setExtent(extent);
    }

    /**
     * 初期データを読み込む。
     * 各パネルのトリガーを実行し、検索ボタンをクリックする。
     */
    loadInitialData() {
        this.#jmaPanel.trigger();
        this.#fnetPanel.trigger();
        this.#searchButton.click();
    }
}
